import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Image, Loader2, Upload, X, Eye, FileText, Sparkles, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '../../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const api = {
  get:  () => axios.get(`${API}/api/tenant-admin/branding`, { withCredentials: true }).then(r => r.data),
  save: (body) => axios.patch(`${API}/api/tenant-admin/branding`, body, { withCredentials: true }).then(r => r.data),
};

export default function BrandingPage() {
  const [branding, setBranding] = useState({
    display_name: '', primary_color: '#1d4ed8', logo_url: '',
    prescription_logo_url: '', letterhead_text: '', prescription_footer_text: '',
    hero_image_url: '', tagline: '', about_long: '', about_highlights: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const rxFileRef = useRef(null);
  const heroFileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { setBranding(await api.get()); } catch (_) {}
      setLoading(false);
    })();
  }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 750 * 1024) {
      toast.error('Logo too large (max 750 KB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, logo_url: reader.result });
    reader.readAsDataURL(f);
  };

  const onPickRxFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) {
      toast.error('Prescription logo too large (max 1 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, prescription_logo_url: reader.result });
    reader.readAsDataURL(f);
  };

  const onPickHeroFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1500 * 1024) {
      toast.error('Hero image too large (max 1.5 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBranding({ ...branding, hero_image_url: reader.result });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.save({
        display_name: branding.display_name,
        primary_color: branding.primary_color,
        logo_url: branding.logo_url || null,
        prescription_logo_url: branding.prescription_logo_url || null,
        letterhead_text: branding.letterhead_text || '',
        prescription_footer_text: branding.prescription_footer_text || '',
        hero_image_url: branding.hero_image_url || null,
        tagline: branding.tagline || '',
        about_long: branding.about_long || '',
        about_highlights: branding.about_highlights || [],
      });
      setBranding(r);
      toast.success('Branding saved — your tenant login and prescription header now use these.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="ta-branding-title"
      >
        Branding
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Customize how your workspace looks to staff and patients on the login screen.
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="bg-white border rounded-xl p-5 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="b-name">Display name</Label>
            <Input id="b-name" value={branding.display_name || ''}
              onChange={(e) => setBranding({ ...branding, display_name: e.target.value })}
              placeholder="Acme Family Clinic" data-testid="ta-branding-name" />
            <p className="text-[11px] text-zinc-500">Shown on the login page and EMR header.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="b-color">Primary color</Label>
            <div className="flex items-center gap-2">
              <input id="b-color" type="color" value={branding.primary_color || '#1d4ed8'}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                className="h-10 w-14 border rounded-md cursor-pointer"
                data-testid="ta-branding-color" />
              <Input value={branding.primary_color || ''}
                onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                className="font-mono text-xs" placeholder="#1d4ed8" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              <div
                className="h-16 w-16 rounded-lg border border-dashed flex items-center justify-center bg-zinc-50 overflow-hidden"
                data-testid="ta-branding-logo-preview"
              >
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="logo preview" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Image className="h-6 w-6 text-zinc-400" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={onPickFile}
                  className="hidden"
                  data-testid="ta-branding-logo-file"
                />
                <Button type="button" variant="outline" size="sm"
                  onClick={() => fileRef.current?.click()}
                  data-testid="ta-branding-logo-pick">
                  <Upload className="h-3.5 w-3.5 mr-1" /> Upload PNG / JPG / SVG
                </Button>
                {branding.logo_url && (
                  <Button type="button" variant="ghost" size="sm" className="text-red-700"
                    onClick={() => setBranding({ ...branding, logo_url: '' })}
                    data-testid="ta-branding-logo-clear">
                    <X className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">Recommended: square, transparent background, ≤ 200×200 px, &lt; 750 KB.</p>
          </div>

          <Button
            onClick={save}
            disabled={saving}
            className="w-full"
            style={{ backgroundColor: branding.primary_color || '#1d4ed8' }}
            data-testid="ta-branding-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save branding
          </Button>
        </div>

        {/* Live login preview */}
        <div className="bg-white border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4 text-zinc-500" />
            <h3 className="font-semibold text-zinc-900">Login preview</h3>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-zinc-100 to-white p-6">
            <div className="bg-white rounded-xl border shadow-sm p-6 max-w-sm mx-auto" data-testid="ta-branding-preview">
              <div className="flex flex-col items-center text-center">
                <div
                  className="h-14 w-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: branding.primary_color || '#1d4ed8' }}
                >
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="logo" className="h-12 w-12 object-contain rounded-xl" />
                  ) : (
                    <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
                      {(branding.display_name || 'C').slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="mt-3 text-base font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
                  {branding.display_name || 'Your clinic'}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">Sign in to your workspace</div>
              </div>
              <div className="space-y-2 mt-5">
                <div className="h-9 rounded border bg-zinc-50" />
                <div className="h-9 rounded border bg-zinc-50" />
                <div
                  className="h-9 rounded text-white text-xs font-medium flex items-center justify-center"
                  style={{ backgroundColor: branding.primary_color || '#1d4ed8' }}
                >
                  Sign in
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Prescription header — separate so it doesn't clutter the login
          editor and so doctors can iterate on print look independently. */}
      <div className="mt-6 bg-white border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-4 w-4 text-blue-700" />
          <h2 className="font-semibold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
            Prescription header
          </h2>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Used on printed prescriptions and the EMR's print preview.
          A different logo from the login one is fine — letterhead images often
          need finer detail than the small login mark.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <div className="space-y-1.5">
              <Label>Prescription logo</Label>
              <div className="flex items-center gap-3">
                <div
                  className="h-20 w-32 rounded-lg border border-dashed flex items-center justify-center bg-zinc-50 overflow-hidden"
                  data-testid="ta-rx-logo-preview"
                >
                  {branding.prescription_logo_url ? (
                    <img src={branding.prescription_logo_url} alt="rx logo"
                         className="max-h-full max-w-full object-contain" />
                  ) : (
                    <FileText className="h-6 w-6 text-zinc-400" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <input
                    ref={rxFileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={onPickRxFile}
                    className="hidden"
                    data-testid="ta-rx-logo-file"
                  />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => rxFileRef.current?.click()}
                    data-testid="ta-rx-logo-pick">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload PNG / JPG / SVG
                  </Button>
                  {branding.prescription_logo_url && (
                    <Button type="button" variant="ghost" size="sm" className="text-red-700"
                      onClick={() => setBranding({ ...branding, prescription_logo_url: '' })}
                      data-testid="ta-rx-logo-clear">
                      <X className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-zinc-500">Recommended: wide rectangular logo, &lt; 1 MB.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Letterhead text</Label>
              <Textarea rows={3}
                value={branding.letterhead_text || ''}
                onChange={(e) => setBranding({ ...branding, letterhead_text: e.target.value })}
                placeholder={"Address line · Phone · Email\nRegistration / NABH / accreditation #\nWebsite"}
                data-testid="ta-rx-letterhead" />
              <p className="text-[11px] text-zinc-500">Shown beside the prescription logo. Newlines preserved.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Footer text</Label>
              <Textarea rows={2}
                value={branding.prescription_footer_text || ''}
                onChange={(e) => setBranding({ ...branding, prescription_footer_text: e.target.value })}
                placeholder={"Signature line · Disclaimer · Follow-up instructions"}
                data-testid="ta-rx-footer" />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" /> Print preview
            </div>
            <div className="border rounded-lg bg-white p-3 text-[10px]" data-testid="ta-rx-preview">
              <div className="flex items-start gap-2 pb-2 border-b">
                {branding.prescription_logo_url ? (
                  <img src={branding.prescription_logo_url} alt="rx logo" className="h-10 w-auto object-contain" />
                ) : (
                  <div className="h-10 w-16 bg-zinc-100 rounded flex items-center justify-center">
                    <FileText className="h-4 w-4 text-zinc-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[11px] text-zinc-900 truncate">
                    {branding.display_name || 'Your Clinic'}
                  </div>
                  {(branding.letterhead_text || '').split('\n').map((line, i) => (
                    <div key={i} className="text-[9px] text-zinc-600 leading-snug whitespace-pre-wrap">{line}</div>
                  ))}
                </div>
              </div>
              <div className="py-3 text-[9px] text-zinc-400 italic text-center">
                Patient details · Rx items · Diagnosis…
              </div>
              {branding.prescription_footer_text && (
                <div className="pt-2 border-t text-[9px] text-zinc-600 whitespace-pre-wrap">
                  {branding.prescription_footer_text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Public landing-page marketing copy — separate so each piece
          can be iterated without disturbing login/Rx visuals. */}
      <div className="mt-6 bg-white border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4 text-purple-700" />
          <h2 className="font-semibold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
            Public landing page
          </h2>
          <span className="ml-2 text-[10px] font-mono text-zinc-500">/c/{branding.display_name ? '<slug>' : 'your-slug'}</span>
        </div>
        <p className="text-sm text-zinc-500 mb-4">
          Tell visitors why your clinic is different. This content powers the public marketing page
          at <span className="font-mono text-zinc-700">/c/&lt;slug&gt;</span>, shown to anyone who clicks your link.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — hero image upload + tagline */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Hero image (background)</Label>
              <div className="flex items-center gap-3">
                <div className="h-24 w-40 rounded-lg border border-dashed flex items-center justify-center bg-zinc-50 overflow-hidden"
                     data-testid="ta-hero-preview">
                  {branding.hero_image_url ? (
                    <img src={branding.hero_image_url} alt="hero" className="h-full w-full object-cover" />
                  ) : (
                    <Image className="h-6 w-6 text-zinc-400" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <input ref={heroFileRef} type="file" accept="image/png,image/jpeg,image/webp"
                         onChange={onPickHeroFile} className="hidden" data-testid="ta-hero-file" />
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => heroFileRef.current?.click()} data-testid="ta-hero-pick">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Upload hero image
                  </Button>
                  {branding.hero_image_url && (
                    <Button type="button" variant="ghost" size="sm" className="text-red-700"
                      onClick={() => setBranding({ ...branding, hero_image_url: '' })}
                      data-testid="ta-hero-clear">
                      <X className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-zinc-500">Wide image, ≤ 1.5 MB. Shown behind your hero text on the landing page.</p>
            </div>

            <div className="space-y-1.5">
              <Label>Tagline (hero sub-headline)</Label>
              <Textarea rows={2}
                value={branding.tagline || ''}
                onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
                placeholder="One sentence — what makes your clinic different?"
                data-testid="ta-tagline" />
            </div>

            <div className="space-y-1.5">
              <Label>About this clinic</Label>
              <Textarea rows={5}
                value={branding.about_long || ''}
                onChange={(e) => setBranding({ ...branding, about_long: e.target.value })}
                placeholder={"2-3 short paragraphs. Why you started. Who you serve.\nSpecialities, accreditations, languages spoken."}
                data-testid="ta-about-long" />
            </div>
          </div>

          {/* RIGHT — highlights bullets editor */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Highlights (3-6 short bullets)</Label>
              <div className="space-y-2">
                {(branding.about_highlights || []).map((bullet, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`ta-highlight-row-${i}`}>
                    <Input value={bullet}
                      onChange={(e) => {
                        const next = [...(branding.about_highlights || [])];
                        next[i] = e.target.value;
                        setBranding({ ...branding, about_highlights: next });
                      }}
                      placeholder="e.g. 30+ years of trusted care"
                      data-testid={`ta-highlight-${i}`} />
                    <Button variant="ghost" size="icon"
                      onClick={() => {
                        const next = (branding.about_highlights || []).filter((_, idx) => idx !== i);
                        setBranding({ ...branding, about_highlights: next });
                      }}
                      data-testid={`ta-highlight-remove-${i}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                ))}
                {(branding.about_highlights || []).length < 6 && (
                  <Button variant="outline" size="sm"
                    onClick={() => setBranding({
                      ...branding,
                      about_highlights: [...(branding.about_highlights || []), ''],
                    })}
                    data-testid="ta-highlight-add">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add highlight
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-zinc-500">
                Each bullet ≤ 120 chars. Shown as a checklist on the landing page.
              </p>
            </div>

            <div>
              <div className="text-xs font-medium text-zinc-600 mb-2 flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" /> Hero preview
              </div>
              <div className="border rounded-lg p-3 text-[10px] relative overflow-hidden" data-testid="ta-hero-preview-card">
                {branding.hero_image_url && (
                  <div className="absolute inset-0 opacity-30"
                       style={{ backgroundImage: `url(${branding.hero_image_url})`,
                                backgroundSize: 'cover', backgroundPosition: 'center' }} />
                )}
                <div className="relative">
                  <div className="font-bold text-[12px] text-zinc-900">
                    {branding.display_name || 'Your Clinic'}
                  </div>
                  <div className="text-[10px] text-zinc-700 mt-1 whitespace-pre-wrap">
                    {branding.tagline || 'Your one-line hero sub-headline shows here.'}
                  </div>
                  <div className="mt-2 inline-block px-2 py-0.5 rounded-full text-white text-[9px]"
                       style={{ background: branding.primary_color }}>
                    Book your visit →
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
